import { LogOut } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { api, errorMessage, isApiError } from '@/api/client';
import { useMe, useUpdateMe } from '@/api/hooks';
import type { User } from '@/api/types';
import { useAuth } from '@/auth/context';
import { Combobox } from '@/components/Combobox';
import { ConfirmDialog } from '@/components/Dialog';
import { useToast } from '@/components/toast-context';
import { Button, Card, ErrorNote, PageHeader, Segmented, SelectField, Skeleton, TextField } from '@/components/ui';
import { useTheme, type ThemeChoice } from '@/lib/theme';
import { CURRENCIES, timezoneLabel, timezones } from '@/lib/timezones';

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <Card as="section" className="grid gap-6 p-5 md:grid-cols-[240px_1fr] md:p-6">
      <div>
        <h2 className="font-semibold">{title}</h2>
        {description && <p className="mt-1 text-sm text-muted-ink">{description}</p>}
      </div>
      <div className="min-w-0">{children}</div>
    </Card>
  );
}

function ProfileForm({ user }: { user: User }) {
  const update = useUpdateMe();
  const toast = useToast();
  const [name, setName] = useState(user.name);
  const [timezone, setTimezone] = useState(user.timezone);
  const [currency, setCurrency] = useState(user.currency);
  const [fields, setFields] = useState<Record<string, string>>({});
  const currencies: string[] = CURRENCIES.includes(user.currency as (typeof CURRENCIES)[number]) ? [...CURRENCIES] : [user.currency, ...CURRENCIES];

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) {
      setFields({ name: 'Name can’t be empty.' });
      return;
    }
    setFields({});
    try {
      await update.mutateAsync({ name: name.trim(), timezone, currency });
      toast({ title: 'Profile saved', description: timezone !== user.timezone ? '“Today” now follows your new time zone.' : undefined });
    } catch (err) {
      if (isApiError(err)) setFields(err.fields);
      toast({ tone: 'error', title: 'Couldn’t save', description: errorMessage(err) });
    }
  }

  return (
    <form onSubmit={submit} noValidate className="flex max-w-md flex-col gap-4">
      <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} autoComplete="name" />
      <TextField label="Email" value={user.email} readOnly disabled hint="Contact support to change your sign-in email." />
      <Combobox label="Time zone" value={timezone} options={timezones()} onChange={setTimezone} format={timezoneLabel} hint="Days, “today” and scheduled updates use this zone." />
      <SelectField label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} hint="A display hint. MyDash doesn’t convert between currencies.">
        {currencies.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </SelectField>
      <div>
        <Button type="submit" variant="primary" loading={update.isPending}>
          Save profile
        </Button>
      </div>
    </form>
  );
}

function PasswordForm() {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const f: Record<string, string> = {};
    if (!current) f.current_password = 'Enter your current password.';
    if (next.length < 8) f.new_password = 'Use at least 8 characters.';
    setFields(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    try {
      await api.put('/v1/me/password', { body: { current_password: current, new_password: next } });
      toast({ title: 'Password changed' });
      setCurrent('');
      setNext('');
    } catch (err) {
      // A 401 here means the current password is wrong (the session itself is still fine).
      if (isApiError(err, 401)) setFields({ current_password: 'That’s not your current password.' });
      else if (isApiError(err)) setFields(err.fields);
      else toast({ tone: 'error', title: 'Couldn’t change the password', description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate className="flex max-w-md flex-col gap-4">
      <TextField label="Current password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} error={fields.current_password} />
      <TextField label="New password" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} error={fields.new_password} hint="At least 8 characters." />
      <div>
        <Button type="submit" loading={busy}>
          Change password
        </Button>
      </div>
    </form>
  );
}

export function SettingsPage() {
  const me = useMe();
  const { signOut, clear } = useAuth();
  const { choice, setTheme } = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const [deleting, setDeleting] = useState(false);
  const [busy, setBusy] = useState(false);

  async function deleteAccount() {
    setBusy(true);
    try {
      await api.delete('/v1/me');
      clear();
      navigate('/sign-in', { replace: true });
      toast({ title: 'Your account was deleted', description: 'All your connections and data are gone.' });
    } catch (err) {
      toast({ tone: 'error', title: 'Couldn’t delete the account', description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Settings" />
      {me.isError && <ErrorNote message={errorMessage(me.error)} onRetry={() => void me.refetch()} />}
      <Section title="Profile" description="Your name, time zone and currency.">
        {me.data ? <ProfileForm key={me.data.id + me.data.timezone} user={me.data} /> : <Skeleton className="h-64" />}
      </Section>
      <Section title="Appearance">
        <Segmented<ThemeChoice>
          label="Theme"
          value={choice}
          onChange={setTheme}
          options={[
            { value: 'system', label: 'System' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
        />
      </Section>
      <Section title="Password">
        <PasswordForm />
      </Section>
      <Section title="Sign out" description="Signs out on this browser.">
        <Button onClick={() => void signOut()} icon={<LogOut className="size-4" aria-hidden />}>
          Sign out
        </Button>
      </Section>
      <Section title="Delete account" description="Removes your account, connections, numbers, updates and alerts. This can’t be undone.">
        <Button variant="danger" onClick={() => setDeleting(true)}>
          Delete my account
        </Button>
      </Section>
      <ConfirmDialog open={deleting} onClose={() => setDeleting(false)} title="Delete your account?" confirmLabel="Delete forever" typeToConfirm="DELETE" loading={busy} onConfirm={() => void deleteAccount()}>
        <p>Everything goes: connections, dashboard, updates, alerts and destinations. Apps you connected stop sharing data with MyDash.</p>
      </ConfirmDialog>
    </div>
  );
}
